<script setup lang="ts">
import type { LibrarySearchMatch } from '@/js/account/entities/document';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { AccountRouteName } from '@/js/account/app/router/route-names';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    match: LibrarySearchMatch
    number: number
    showDocumentTitle?: boolean
  }>(),
  { showDocumentTitle: false },
);

const cn = classNames('library-search-snippet');

const matchRoute = computed(() => ({
  name: AccountRouteName.LibraryDocument,
  params: { id: String(props.match.document.id) },
  query: {
    matchTerm: props.match.snippet.match.term,
    matchIndex: String(props.match.snippet.match.occurrence),
  },
}));

const linkLabel = computed(
  () => `Open ${props.match.document.title} at match ${props.number}`,
);
</script>

<template>
  <li :class="cn()">
    <span :class="cn('number')" aria-hidden="true">{{ number }}</span>
    <!-- The visible counter is decorative, and mobile drops it altogether. -->
    <span class="visually-hidden">Match {{ number }}</span>
    <div :class="cn('body')">
      <p v-if="showDocumentTitle" :class="cn('document')">
        {{ match.document.title }}
      </p>
      <!--
        Both branches must be elements: Vue condenses whitespace between
        elements only, so a bare interpolation would leak a space into the text.
      -->
      <p :class="cn('text')">
        <span v-if="match.snippet.prefix">{{ match.snippet.prefix }}</span>
        <template v-for="(part, index) in match.snippet.parts" :key="index">
          <mark v-if="part.highlighted" :class="cn('mark')">{{ part.text }}</mark>
          <span v-else>{{ part.text }}</span>
        </template>
        <span v-if="match.snippet.suffix">{{ match.snippet.suffix }}</span>
      </p>
      <RouterLink :to="matchRoute" :class="cn('link')" :aria-label="linkLabel">
        Open at this match →
      </RouterLink>
    </div>
  </li>
</template>

<style lang="scss" scoped>
.library-search-snippet {
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-background-secondary);

  &__number {
    flex: none;
    width: 24px;
    padding-top: 2px;
    color: var(--color-text-accent);
    font-size: 13px;
    text-align: right;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  &__document {
    margin: 0;
    color: var(--color-primary);
    font-size: 13px;
    font-weight: var(--font-weight-medium);
    letter-spacing: 0.04em;
    overflow-wrap: anywhere;
  }

  &__text {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: 15px;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }

  &__mark {
    padding: 1px 3px;
    border-radius: 3px;
    background-color: var(--color-background-primary);
    color: var(--color-primary-dark);
    font-weight: var(--font-weight-medium);
  }

  &__link {
    align-self: flex-start;
    color: var(--color-primary);
    font-size: 13px;
    font-weight: var(--font-weight-medium);
    text-decoration: none;

    &:hover {
      color: var(--color-primary-dark);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;

    &__number {
      display: none;
    }

    &__link {
      display: flex;
      align-items: center;
      min-height: 44px;
    }
  }
}
</style>
